import apiService from './core/apiService';
import { connectivityGuard } from './core/connectivityGuard';
import { devLogger } from '../utils/devLogger';
const PUSH_SUBSCRIPTION_KEY = 'icavi:push-subscription-attempt';
const ATTEMPT_COOLDOWN_MS = 12 * 60 * 60 * 1000; // 12 horas
const isPushSupported = () => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isSecure = typeof window !== 'undefined' &&
        (window.isSecureContext || hostname === 'localhost' || hostname === '127.0.0.1');
    if (!isSecure) {
        return false;
    }
    return (typeof window !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window);
};
const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(new ArrayBuffer(rawData.length));
    for (let i = 0; i < rawData.length; i += 1) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};
const deveTentarInscricao = () => {
    try {
        const lastAttempt = localStorage.getItem(PUSH_SUBSCRIPTION_KEY);
        if (!lastAttempt) {
            return true;
        }
        const lastAttemptTime = Number(lastAttempt);
        return Number.isNaN(lastAttemptTime) || Date.now() - lastAttemptTime > ATTEMPT_COOLDOWN_MS;
    }
    catch {
        return true;
    }
};
const registrarTentativa = () => {
    try {
        localStorage.setItem(PUSH_SUBSCRIPTION_KEY, String(Date.now()));
    }
    catch {
        // Ignorar falhas de storage
    }
};
export const inicializarPushNotifications = async (options = {}) => {
    devLogger.log('Push: Iniciando inicializarPushNotifications, force:', options.force);
    if (!isPushSupported()) {
        devLogger.warn('Push: Nao suportado neste contexto');
        return { ok: false, error: 'Push nao suportado ou contexto inseguro.' };
    }
    const permission = Notification.permission;
    devLogger.log('Push: Permissao atual:', permission);
    if (permission === 'denied') {
        devLogger.warn('Push: Permissao negada pelo usuario');
        return { ok: false, error: 'Permissao de notificacao negada no navegador.' };
    }
    const force = options.force === true;
    const refreshIfBackendEmpty = options.refreshIfBackendEmpty !== false;
    if (!force && permission !== 'granted') {
        if (!deveTentarInscricao()) {
            devLogger.log('Push: Tentativa recente, ignorando');
            return { ok: false, error: 'Tentativa recente. Aguarde e tente novamente.' };
        }
        registrarTentativa();
        return { ok: false, error: 'Permissao ainda nao concedida.' };
    }
    let granted = permission === 'granted';
    if (!granted) {
        // Solicita permissão ao navegador
        devLogger.log('Push: Solicitando permissao ao navegador...');
        try {
            const result = await Notification.requestPermission();
            devLogger.log('Push: Resultado da solicitacao:', result);
            granted = result === 'granted';
        }
        catch (err) {
            console.error('Push: Erro ao solicitar permissao:', err);
            granted = false;
        }
    }
    if (!granted) {
        return { ok: false, error: 'Permissao nao concedida.' };
    }
    let publicKey = '';
    try {
        const response = await apiService.get('/api/push/public-key');
        publicKey = response?.public_key || response?.publicKey || response?.dados?.public_key || '';
    }
    catch (error) {
        return { ok: false, error: 'Falha ao buscar VAPID public key.' };
    }
    if (!publicKey) {
        return { ok: false, error: 'VAPID public key nao encontrada.' };
    }
    let backendSubscriptions = null;
    if (refreshIfBackendEmpty) {
        try {
            const statusBackend = await apiService.get('/api/push/status');
            const totalRaw = statusBackend?.subscriptions_ativas ??
                statusBackend?.subscriptionsAtivas ??
                statusBackend?.dados?.subscriptions_ativas;
            const total = Number(totalRaw);
            backendSubscriptions = Number.isNaN(total) ? null : total;
        }
        catch {
            backendSubscriptions = null;
        }
    }
    try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        let subscription = await registration.pushManager.getSubscription();
        const deveRecriarSubscription = backendSubscriptions === 0;
        // Verifica se subscription existente é válida
        if (subscription) {
            const subJson = subscription.toJSON();
            const isValid = subJson.endpoint && subJson.keys?.p256dh && subJson.keys?.auth;
            if (!isValid) {
                devLogger.warn('Push: Subscription existente invalida, removendo...');
                await subscription.unsubscribe();
                subscription = null;
            }
        }
        if (subscription && deveRecriarSubscription) {
            devLogger.warn('Push: Backend sem subscriptions ativas, recriando...');
            try {
                await subscription.unsubscribe();
            }
            catch (error) {
                devLogger.warn('Push: Falha ao remover subscription antiga:', error);
            }
            subscription = null;
        }
        if (subscription) {
            const subJson = subscription.toJSON();
            devLogger.log('Push: Enviando subscription existente para backend...');
            const resposta = await apiService.post('/api/push/subscribe', { subscription: subJson });
            if (resposta?.status !== 'sucesso' && resposta?.sucesso !== true) {
                return {
                    ok: false,
                    error: resposta?.mensagem || 'Falha ao salvar subscription no servidor.',
                };
            }
            return { ok: true };
        }
        // Cria nova subscription - isso pode falhar se permissão não foi concedida
        devLogger.log('Push: Criando nova subscription...');
        const newSubscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
        const newSubJson = newSubscription.toJSON();
        // Valida antes de enviar
        if (!newSubJson.endpoint || !newSubJson.keys?.p256dh || !newSubJson.keys?.auth) {
            console.error('Push: Nova subscription criada mas incompleta:', {
                hasEndpoint: Boolean(newSubJson.endpoint),
                hasP256dh: Boolean(newSubJson.keys?.p256dh),
                hasAuth: Boolean(newSubJson.keys?.auth),
            });
            return { ok: false, error: 'Subscription criada incompleta pelo navegador.' };
        }
        devLogger.log('Push: Enviando nova subscription para backend...');
        const resposta = await apiService.post('/api/push/subscribe', { subscription: newSubJson });
        if (resposta?.status !== 'sucesso' && resposta?.sucesso !== true) {
            return {
                ok: false,
                error: resposta?.mensagem || 'Falha ao salvar subscription no servidor.',
            };
        }
        return { ok: true };
    }
    catch (error) {
        const mensagemErro = error?.response?.data?.mensagem ||
            error?.message;
        if (!connectivityGuard.isExpectedTransient(error)) {
            console.error('Push: Erro ao registrar subscription:', error);
        }
        return { ok: false, error: mensagemErro || 'Falha ao criar subscription no navegador.' };
    }
};
/**
 * Registra listener para mensagens do Service Worker.
 * O SW não tem acesso à Audio API, então envia mensagem para a página tocar o som.
 * Retorna função de cleanup para remover o listener.
 */
export const registrarListenerSomSW = () => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
        return () => { };
    }
    const handler = (event) => {
        if (event.data?.type === 'PLAY_NOTIFICATION_SOUND') {
            try {
                const audio = new Audio('/notification.mp3');
                audio.volume = 0.5;
                audio.play().catch(() => {
                    // Ignorar erros de autoplay / fonte não suportada
                });
            }
            catch {
                // Ignorar erros de autoplay
            }
        }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
};
/**
 * Verifica se a subscription push ainda é válida no backend e renova se necessário.
 * Útil para chamar periodicamente ou no login.
 */
export const verificarEAtualizarSubscription = async () => {
    if (!isPushSupported() || Notification.permission !== 'granted') {
        return;
    }
    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (!subscription)
            return;
        // Verifica se o backend ainda tem essa subscription
        const status = await import('./core/apiService').then(m => m.default.get('/api/push/status'));
        const total = Number(status?.subscriptions_ativas ?? status?.dados?.subscriptions_ativas ?? 0);
        // Backend sem subscriptions — reenviar a subscription local para sincronizar
        if (!Number.isNaN(total) && total === 0) {
            const subJson = subscription.toJSON();
            if (subJson.endpoint && subJson.keys?.p256dh && subJson.keys?.auth) {
                const apiService = await import('./core/apiService').then(m => m.default);
                await apiService.post('/api/push/subscribe', { subscription: subJson });
            }
        }
    }
    catch {
        // Falha silenciosa — verificação é best-effort
    }
};
export const removerPushSubscription = async () => {
    if (!isPushSupported()) {
        return;
    }
    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
            return;
        }
        await apiService.post('/api/push/unsubscribe', { endpoint: subscription.endpoint });
        await subscription.unsubscribe();
    }
    catch (error) {
        devLogger.warn('Falha ao remover push subscription:', error);
    }
};
